import { useSyncExternalStore } from "react";
import { Box } from "../../ui/box";
import { Text } from "../../ui/text";
import ThinkingIndicator from "./ThinkingIndicator";
import type { AgentTurnStore } from "./turnStore";

type Props = {
  store: AgentTurnStore;
  /**
   * Show a spinner instead of the text. Either nothing has been generated yet, or
   * we reconnected mid-run and the partial we hold is missing its middle — an
   * honest spinner beats a sentence that silently starts nowhere.
   */
  thinking: boolean;
  /** Feeds the elapsed-time cue on `ThinkingIndicator`. 0 while idle. */
  startedAt: number;
  /**
   * Whether to fall back to a "Pensando…" indicator when there is no text yet.
   *
   * False while a moment card or the stage caption is on screen: it already says the
   * agent is working, and in more detail, so a second "Pensando…" bubble stacked under
   * it is just noise. Once real tokens arrive this bubble appears regardless — the
   * suppression is only of the empty, thinking-only state.
   */
  thinkingFallback?: boolean;
};

/**
 * The answer as it is being written.
 *
 * The ONLY component in the tree that subscribes to the token stream, which is the
 * whole point: deltas land 30-100 times a second, and if they went through React
 * state the entire transcript would re-render on every token. Here, nothing above
 * this bubble even knows a token arrived.
 *
 * Rendered as plain text, not markdown, deliberately. Re-parsing a markdown string
 * that grows to a few kilobytes, twenty times a second, is quadratic and it is the
 * single worst thing you can do to a mid-range Android. The persisted row that
 * replaces this bubble a moment later renders as markdown.
 */
const StreamingBubble = ({
  store,
  thinking,
  startedAt,
  thinkingFallback = true,
}: Props) => {
  const text = useSyncExternalStore(store.subscribeText, store.getText);

  if (thinking || !text) {
    return thinkingFallback ? (
      <ThinkingIndicator startedAt={startedAt} />
    ) : null;
  }

  return (
    // The hairline brand border is the entire "still being written" signal. The FILL
    // deliberately matches the settled assistant bubble, so when the persisted row
    // replaces this one there is no colour jump — only the border quietly going away.
    <Box
      aria-busy
      className="mb-3 ml-[30px] rounded-2xl rounded-bl-md border border-brand-100 bg-background-50 px-3.5 py-2.5 animate-fade-rise motion-reduce:animate-none"
    >
      <Text className="text-sm leading-5 text-typography-900">{text}</Text>
    </Box>
  );
};

export default StreamingBubble;
